import { partnerKeys } from '@/constant/queryKeys';
import { apiRequestV2 } from '@/lib/queryClient';
import type { Partner, ProjectTimeline, PartnerJoinStatus, PartnerJoinRequest } from "@/types/xdao";
import { queryOptions, mutationOptions } from '@tanstack/react-query';

export function getPartnersOptions() {
    return queryOptions({
        queryKey: partnerKeys.all,
        queryFn: () => apiRequestV2<Partner[]>('GET', '/api/v1/partner'),
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        placeholderData: (previousData) => previousData,
    });
}

export function getPartnerByIdOptions(partnerId: number) {
    return queryOptions({
        queryKey: partnerKeys.getById(partnerId),
        queryFn: () => apiRequestV2<Partner>('GET', `/api/v1/partner/${partnerId}`),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
}

export function getPartnerBySlugOptions(slug?: string) {
    return queryOptions({
        queryKey: partnerKeys.getBySlug(slug),
        queryFn: () => apiRequestV2<Partner>('GET', `/api/v1/partner/by-slug/${slug}`),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
        enabled: !!slug,
    });
}

export function getPartnerProjectsByIdOptions(partnerId?: number) {
    return queryOptions({
        queryKey: partnerKeys.getProjectsById(partnerId),
        queryFn: () => apiRequestV2<ProjectTimeline[]>('GET', `/api/v1/partner/${partnerId}/projects`),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
        enabled: !!partnerId,
    });
}

export function getPartnerJoinStatusByIdOptions(partnerId?: number) {
    return queryOptions({
        queryKey: partnerKeys.getJoinStatusById(partnerId),
        queryFn: () => apiRequestV2<PartnerJoinStatus>('GET', `/api/v1/partner/${partnerId}/join/status`),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
        enabled: !!partnerId,
    });
}

export function getPartnerLastActiveProjectOptions(partnerId?: number) {
    return queryOptions({
        queryKey: partnerKeys.getLastActiveProject(partnerId),
        queryFn: () => apiRequestV2<ProjectTimeline & {isExpired: boolean}>('GET', `/api/v1/partner/${partnerId}/last-active-project`),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
        enabled: !!partnerId,
    });
}

export function postJoinPartnerOptions() {
    return mutationOptions({
        mutationKey: partnerKeys.postJoin,
        mutationFn: ({ partnerId }: { partnerId: number }) => apiRequestV2<PartnerJoinRequest>('POST', '/api/v1/partner/join', { partnerId }),
    });
}
